// Profile › Memory (RECALL-PEOPLE-SURFACES; design §5.1), on the account page, one per company a
// person belongs to. Three small things and nothing else:
//   - what memory knows about me, each line with Fix and Forget (hover icons; small actions are
//     modals); "forget this" is the record picker, a preview, a confirm and a 24-hour undo;
//   - the holds that cover me (a case reference only);
//   - "Remember my DMs" (off unless turned on).
// Actions the memory service does not offer yet read "Waiting on memory service" and never error;
// the request stands until the door opens. No explainer paragraphs: explanations sit behind (i).

import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  InfoTip,
  Label,
  SettingsGroup,
  SettingsRow,
  Switch,
  Textarea,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EraserIcon, PencilIcon, Undo2Icon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import { formatDateTime } from "../format";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { ROW_HOVER, RowAction } from "../settings/row-actions";
import { problemCode } from "../structure/parts";

type Me = OperationOutput<"memoryMe.get">;
type Line = Me["about"]["lines"][number];
type Forgets = OperationOutput<"memoryForget.list">;
type ForgetResult = OperationOutput<"memoryForget.step">;

export const memoryMeKey = (co: string) => ["company", co, "memory", "me"] as const;
export const memoryForgetKey = (co: string) => ["company", co, "memory", "forget-list"] as const;

export function ProfileMemory({
  api,
  co,
  title = "Memory",
  id = "profile-memory",
}: {
  api: Api;
  co: string;
  title?: string;
  /** The section's anchor (unique on the page: one per company). */
  id?: string;
}) {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: memoryMeKey(co),
    queryFn: () => api.client.memoryMe.get({ params: { co } }),
    retry: false,
  });
  const forgets = useQuery({
    queryKey: memoryForgetKey(co),
    queryFn: () => api.client.memoryForget.list({ params: { co } }),
    retry: false,
    enabled: me.isSuccess,
  });
  const [fixing, setFixing] = useState<Line | null>(null);
  const [forgetting, setForgetting] = useState<{ pick: Line | null } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const act = useMutation({
    mutationFn: (body: Parameters<Api["client"]["memoryMe"]["act"]>[0]["body"]) =>
      api.client.memoryMe.act({ params: { co }, body }),
    onSuccess: (r) => {
      qc.setQueryData(memoryMeKey(co), r.view);
      setNotice(r.waiting);
    },
  });
  // The memory add-on is off, or this is not a person's own session: nothing of theirs to show.
  const code = problemCode(me.error);
  if (code === "forbidden" || code === "not_found" || code === "feature_disabled") return null;
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: memoryMeKey(co) });
    void qc.invalidateQueries({ queryKey: memoryForgetKey(co) });
  };
  return (
    <SettingsGroup id={id} title={title} data-slot="profile-memory">
      {me.isPending ? (
        <Loading label="Loading your memory" rows={2} />
      ) : me.isError ? (
        <LoadError what="Your memory" error={me.error} />
      ) : (
        <>
          <MemoryLines
            me={me.data}
            onFix={setFixing}
            onForget={(pick) => setForgetting({ pick })}
            onForgetOther={() => setForgetting({ pick: null })}
          />
          {notice ? (
            <p role="status" className="px-4 pb-2 text-[13px] text-muted-foreground">
              {notice}
            </p>
          ) : null}
          <SettingsRow
            label={
              <span className="flex items-center gap-1">
                Holds
                <InfoTip label="About holds">
                  A legal hold pauses forgetting. It shows the case, not what is held.
                </InfoTip>
              </span>
            }
            data-slot="memory-my-holds"
          >
            {me.data.holds.length === 0 ? (
              <span className="text-[13px] text-muted-foreground">None</span>
            ) : (
              <span className="flex flex-wrap gap-1">
                {me.data.holds.map((h) => (
                  <Badge key={h.id} variant="danger">
                    {h.case_ref}
                  </Badge>
                ))}
              </span>
            )}
          </SettingsRow>
          <SettingsRow
            label={
              <span className="flex items-center gap-1">
                <Label htmlFor={`${id}-dms`}>Remember my DMs</Label>
                <InfoTip label="About Remember my DMs">
                  Off unless you turn it on. Then your own words in one-to-one DMs go into your own
                  memory. Never the other person&rsquo;s words, never group or agent DMs.
                </InfoTip>
              </span>
            }
            data-slot="memory-dms"
          >
            <span className="flex items-center gap-2">
              {me.data.remember_dms.on && !me.data.remember_dms.mirrored ? (
                <Badge variant="warning">{me.data.capabilities.waiting_text}</Badge>
              ) : null}
              <Switch
                id={`${id}-dms`}
                checked={me.data.remember_dms.on}
                disabled={act.isPending}
                onCheckedChange={(on) => act.mutate({ action: "dms", on })}
              />
            </span>
          </SettingsRow>
          <Recent
            items={forgets.data?.items ?? []}
            api={api}
            co={co}
            waiting={me.data.capabilities.waiting_text}
            onChange={refresh}
          />
          {act.isError ? (
            <p role="alert" className="px-4 pb-3 text-[13px] text-destructive">
              {problemText(act.error, "That did not go through.")}
            </p>
          ) : null}
        </>
      )}
      {fixing ? (
        <FixModal
          line={fixing}
          pending={act.isPending}
          error={act.isError ? problemText(act.error, "That did not go through.") : null}
          onClose={() => setFixing(null)}
          // closes only when the fix was kept; "waiting" leaves the words in the modal to retry
          onSave={async (text) => {
            try {
              const r = await act.mutateAsync({
                action: "fix",
                type: fixing.type,
                title: fixing.title,
                text,
              });
              if (!r.waiting) setFixing(null);
              return r.waiting;
            } catch {
              return null;
            }
          }}
        />
      ) : null}
      {forgetting && me.data ? (
        <ForgetModal
          api={api}
          co={co}
          lines={me.data.about.lines.filter((l) => l.target !== "")}
          start={forgetting.pick}
          waiting={me.data.capabilities.waiting_text}
          onClose={() => {
            setForgetting(null);
            refresh();
          }}
        />
      ) : null}
    </SettingsGroup>
  );
}

function MemoryLines({
  me,
  onFix,
  onForget,
  onForgetOther,
}: {
  me: Me;
  onFix: (l: Line) => void;
  onForget: (l: Line) => void;
  onForgetOther: () => void;
}) {
  const lines = me.about.lines;
  return (
    <div className="flex flex-col" data-slot="memory-about">
      <div className="flex items-center justify-between gap-2 px-4 pt-3">
        <span className="flex items-center gap-1 text-[13px] font-medium">
          What memory knows about me
          <InfoTip label="About what memory knows">
            Notes kept about you, in your own space. Only you see them. Fix a line or forget it.
          </InfoTip>
        </span>
        {lines.some((l) => l.target !== "") ? (
          <Button size="sm" variant="outline" onClick={onForgetOther}>
            Forget…
          </Button>
        ) : null}
      </div>
      {me.about.waiting ? (
        <p className="px-4 py-3 text-[13px]">
          <Badge variant="warning">{me.about.waiting}</Badge>
        </p>
      ) : lines.length === 0 ? (
        <p className="px-4 py-3 text-[13px] text-muted-foreground">Nothing yet.</p>
      ) : (
        <ul className="flex flex-col py-1">
          {lines.map((l) => (
            <li
              key={`${l.type}/${l.title}`}
              className="group/row flex items-start gap-3 px-4 py-2 hover:bg-muted/50"
              data-line={l.title}
            >
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-medium">{l.title}</span>
                <span className="block text-[13px] text-muted-foreground">{l.text}</span>
              </span>
              <span className={`flex shrink-0 items-center gap-0.5 ${ROW_HOVER}`}>
                <RowAction
                  icon={PencilIcon}
                  label={`Fix ${l.title}`}
                  tip="Fix"
                  onClick={() => onFix(l)}
                />
                {l.target !== "" ? (
                  <RowAction
                    icon={EraserIcon}
                    label={`Forget ${l.title}`}
                    tip="Forget"
                    className="hover:text-danger-text"
                    onClick={() => onForget(l)}
                  />
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FixModal({
  line,
  pending,
  error,
  onClose,
  onSave,
}: {
  line: Line;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  /** Resolves to "waiting" text when the memory service could not take the fix yet. */
  onSave: (text: string) => Promise<string | null>;
}) {
  const [text, setText] = useState(line.text);
  const [waiting, setWaiting] = useState<string | null>(null);
  const same = text.trim() === line.text.trim();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-slot="memory-fix">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!same && text.trim()) void onSave(text.trim()).then(setWaiting);
          }}
        >
          <DialogHeader>
            <DialogTitle>Fix “{line.title}”</DialogTitle>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="memory-fix-text">What it should say</FieldLabel>
            <Textarea
              id="memory-fix-text"
              rows={4}
              maxLength={2000}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setWaiting(null);
              }}
            />
          </Field>
          {waiting ? (
            <p role="status" className="text-[13px] text-muted-foreground">
              {waiting}. Your words stay here until it is ready.
            </p>
          ) : null}
          {error ? (
            <p role="alert" className="text-[13px] text-destructive">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button type="button" variant="outline" />}>Cancel</DialogClose>
            <Button type="submit" disabled={pending || same || !text.trim()}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Forget this: pick one of my own lines, see what would go, confirm, and undo for 24 hours. The
 * confirm token lives in this dialog only. A confirm that gets no clear answer leaves the request
 * previewed, and the same Confirm is tried again with the same token.
 */
function ForgetModal({
  api,
  co,
  lines,
  start,
  waiting,
  onClose,
}: {
  api: Api;
  co: string;
  lines: Line[];
  start: Line | null;
  waiting: string;
  onClose: () => void;
}) {
  const [pick, setPick] = useState<Line | null>(start);
  const [token, setToken] = useState<string | null>(null);
  const [req, setReq] = useState<ForgetResult["request"] | null>(null);
  const [wait, setWait] = useState<string | null>(null);
  const step = useMutation({
    mutationFn: (
      body: Parameters<Api["client"]["memoryForget"]["step"]>[0]["body"],
    ): Promise<ForgetResult> => api.client.memoryForget.step({ params: { co }, body }),
    onSuccess: (r) => {
      setReq(r.request);
      setWait(r.waiting);
      if (r.confirm_token) setToken(r.confirm_token);
      if (r.request.state !== "previewed") setToken(null);
    },
  });
  const state = req?.state ?? null;
  const failed = step.isError ? problemText(step.error, "That did not go through.") : null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-slot="memory-forget">
        <DialogHeader>
          <DialogTitle>Forget this</DialogTitle>
        </DialogHeader>
        {state === null ? (
          <fieldset className="grid gap-1" data-slot="forget-picker">
            <legend className="sr-only">Pick a line to forget</legend>
            {lines.map((l) => (
              <label
                key={`${l.type}/${l.title}`}
                className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-[13px] hover:bg-muted"
              >
                <input
                  type="radio"
                  name="forget-line"
                  className="mt-1"
                  checked={pick?.target === l.target}
                  onChange={() => setPick(l)}
                />
                <span className="min-w-0">
                  <span className="block font-medium">{l.title}</span>
                  <span className="block text-muted-foreground">{l.text}</span>
                </span>
              </label>
            ))}
          </fieldset>
        ) : (
          <ForgetStatus req={req} waiting={wait ?? (state === "waiting" ? waiting : null)} />
        )}
        {failed ? (
          <p role="alert" className="text-[13px] text-destructive">
            {failed}
          </p>
        ) : null}
        <DialogFooter>
          {state === null ? (
            <>
              <DialogClose render={<Button type="button" variant="outline" />}>Cancel</DialogClose>
              <Button
                disabled={!pick || step.isPending}
                onClick={() =>
                  pick && step.mutate({ step: "preview", scope: "record", target: pick.target })
                }
              >
                Preview
              </Button>
            </>
          ) : state === "previewed" && req ? (
            <>
              <Button
                variant="outline"
                disabled={step.isPending}
                onClick={() => {
                  step.mutate({ step: "dismiss", request_id: req.id });
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={step.isPending || !token}
                onClick={() => token && step.mutate({ step: "confirm", request_id: req.id, token })}
              >
                Forget
              </Button>
            </>
          ) : state === "confirmed" && req ? (
            <>
              <Button
                variant="outline"
                disabled={step.isPending}
                onClick={() => step.mutate({ step: "undo", request_id: req.id })}
              >
                Undo
              </Button>
              <Button onClick={onClose}>Done</Button>
            </>
          ) : (
            <Button onClick={onClose}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ForgetStatus({
  req,
  waiting,
}: {
  req: ForgetResult["request"] | null;
  waiting: string | null;
}) {
  if (!req) return null;
  const kinds = req.preview
    ? Object.entries(req.preview.by_kind)
        .map(([k, n]) => `${n} ${k.toLowerCase()}${n === 1 ? "" : "s"}`)
        .join(", ")
    : "";
  return (
    <div
      className="grid gap-1 text-[13px]"
      role="status"
      data-slot="forget-status"
      data-state={req.state}
    >
      <p className="font-medium">{req.label}</p>
      {req.state === "previewed" ? (
        <p>
          {req.preview?.count ?? 0} to forget{kinds ? ` (${kinds})` : ""}. You can undo for 24
          hours.
        </p>
      ) : req.state === "confirmed" ? (
        <p>Forgotten. Undo until {req.undo_until ? formatDateTime(req.undo_until) : "tomorrow"}.</p>
      ) : req.state === "waiting" ? (
        <p>
          <Badge variant="warning">{waiting ?? "Waiting on memory service"}</Badge>
        </p>
      ) : req.state === "blocked" ? (
        <p>On hold{req.case_ref ? ` (${req.case_ref})` : ""}. Nothing was forgotten.</p>
      ) : req.state === "undone" ? (
        <p>Undone. It is back.</p>
      ) : req.state === "expired" ? (
        <p>That preview ran out. Start again.</p>
      ) : (
        <p>It did not go through. Try again.</p>
      )}
    </div>
  );
}

/** Forgets still inside their 24 hours, and any waiting on the memory service. */
function Recent({
  items,
  api,
  co,
  waiting,
  onChange,
}: {
  items: Forgets["items"];
  api: Api;
  co: string;
  waiting: string;
  onChange: () => void;
}) {
  const undo = useMutation({
    mutationFn: (request_id: string) =>
      api.client.memoryForget.step({ params: { co }, body: { step: "undo", request_id } }),
    onSettled: onChange,
  });
  const open = items.filter(
    (f) => f.state === "confirmed" || f.state === "waiting" || f.state === "blocked",
  );
  if (open.length === 0) return null;
  return (
    <div className="flex flex-col px-4 py-2" data-slot="memory-recent">
      <p className="text-[13px] font-medium">Forgotten</p>
      <ul className="flex flex-col">
        {open.map((f) => (
          <li
            key={f.id}
            className="group/row flex items-center gap-2 py-1 text-[13px]"
            data-forget={f.state}
          >
            <span className="min-w-0 flex-1 truncate">{f.label}</span>
            {f.state === "confirmed" ? (
              <>
                <span className="text-muted-foreground">
                  Undo until {f.undo_until ? formatDateTime(f.undo_until) : ""}
                </span>
                <RowAction
                  icon={Undo2Icon}
                  label={`Undo forgetting ${f.label}`}
                  tip="Undo"
                  className="opacity-100"
                  disabled={undo.isPending}
                  onClick={() => undo.mutate(f.id)}
                />
              </>
            ) : f.state === "waiting" ? (
              <Badge variant="warning">{waiting}</Badge>
            ) : (
              <Badge variant="danger">On hold{f.case_ref ? ` (${f.case_ref})` : ""}</Badge>
            )}
          </li>
        ))}
      </ul>
      {undo.isError ? (
        <p role="alert" className="text-[13px] text-destructive">
          {problemText(undo.error, "That did not go through.")}
        </p>
      ) : null}
    </div>
  );
}
